import React, { useEffect, useState, useCallback } from 'react';
import { approvalAPI, voucherAPI, advanceAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { CheckSquare, CheckCircle, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function ApprovalsPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ vouchers: [], advances: [] });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await approvalAPI.list();
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const approveVoucher = async (id) => {
    try {
      await voucherAPI.approve(id);
      toast.success('Voucher approved');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  const rejectVoucher = async (id) => {
    const reason = window.prompt('Rejection reason:');
    if (reason === null) return;
    try {
      await voucherAPI.reject(id, reason);
      toast.success('Voucher rejected');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  const approveAdvance = async (id) => {
    try {
      await advanceAPI.approve(id);
      toast.success('Advance approved');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  const rejectAdvance = async (id) => {
    const reason = window.prompt('Rejection reason:');
    if (reason === null) return;
    try {
      await advanceAPI.reject(id, reason);
      toast.success('Advance rejected');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <div className="flex items-center gap-2">
        <CheckSquare className="w-5 h-5 text-slate-700" />
        <h1 className="text-xl font-bold tracking-tight text-slate-900">Pending Approvals</h1>
      </div>

      {/* Pending Vouchers */}
      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="px-4 py-3 border-b border-slate-200">
          <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Vouchers ({data.vouchers?.length || 0})
          </h2>
        </div>
        {data.vouchers?.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {data.vouchers.map(v => (
              <div key={v._id} className="p-3 flex items-center justify-between hover:bg-slate-50" data-testid={`approval-voucher-${v.voucherId}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Link to={`/vouchers/${v._id}`} className="text-xs font-medium text-slate-900 hover:underline">{v.voucherId}</Link>
                    <span className="text-[10px] text-slate-400">{v.voucherType}</span>
                  </div>
                  <p className="text-xs text-slate-500 truncate mt-0.5">{v.description}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">By: {v.createdBy?.name}</p>
                </div>
                <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                  <span className="text-sm font-bold text-slate-900">₹{v.amount?.toLocaleString('en-IN')}</span>
                  {v.createdBy?._id !== user?.id && (
                    <>
                      <Button size="sm" onClick={() => approveVoucher(v._id)} className="bg-green-600 hover:bg-green-700 text-white text-[10px] h-6 px-2" data-testid={`approve-voucher-${v.voucherId}`}>
                        <CheckCircle className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => rejectVoucher(v._id)} className="text-[10px] h-6 px-2" data-testid={`reject-voucher-${v.voucherId}`}>
                        <XCircle className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400 text-center py-8">No pending vouchers</p>
        )}
      </div>

      {/* Pending Advances */}
      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="px-4 py-3 border-b border-slate-200">
          <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Advances ({data.advances?.length || 0})
          </h2>
        </div>
        {data.advances?.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {data.advances.map(a => (
              <div key={a._id} className="p-3 flex items-center justify-between hover:bg-slate-50" data-testid={`approval-advance-${a.advanceId}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-900">{a.advanceId}</span>
                  </div>
                  <p className="text-xs text-slate-500 truncate mt-0.5">{a.purpose}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">By: {a.employee?.name}</p>
                </div>
                <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                  <span className="text-sm font-bold text-slate-900">₹{a.amount?.toLocaleString('en-IN')}</span>
                  {a.employee?._id !== user?.id && (
                    <>
                      <Button size="sm" onClick={() => approveAdvance(a._id)} className="bg-green-600 hover:bg-green-700 text-white text-[10px] h-6 px-2" data-testid={`approve-advance-${a.advanceId}`}>
                        <CheckCircle className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => rejectAdvance(a._id)} className="text-[10px] h-6 px-2" data-testid={`reject-advance-${a.advanceId}`}>
                        <XCircle className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400 text-center py-8">No pending advances</p>
        )}
      </div>
    </div>
  );
}
